import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import apiClient from "../../common/http.service";
import { useCategories } from "../../hooks/useCategories";
import { useProducts, useProduct } from "../../hooks/useProducts";
import { COUNTRY_OPTIONS } from "../../common/countries";

function SkuCombinationBuilder({ categoryId, categories, variants, setVariants, basePrice }) {
    const selectedCategory = categories.find((c) => c._id === categoryId);
    const [uploadingIndex, setUploadingIndex] = useState(null);

    const rawNames = selectedCategory?.names || [];
    const attributes = rawNames.map(n => {
        if (typeof n === 'string') return { _id: n, name: n };
        return { _id: n._id || n.id, name: n.name };
    }).filter(n => n.name);

    useEffect(() => {
        if (categoryId && attributes.length > 0 && variants.length === 0) {
            const initialAttributes = {};
            attributes.forEach(attr => {
                const isColor = attr.name.toLowerCase() === 'color' || attr.name.toLowerCase() === 'colour';
                initialAttributes[attr._id] = { value: isColor ? "#000000" : "", price: "" };
            });

            setVariants([{ sku: "", price: basePrice || "", stock: 0, image: "", attributes: initialAttributes }]);
        }
    }, [categoryId, attributes.length, variants.length, basePrice, setVariants]);

    if (!categoryId) {
        return (
            <div className="text-center p-5 border rounded bg-light" style={{ borderStyle: "dashed" }}>
                <i className="fas fa-layer-group fa-2x text-muted mb-2"></i>
                <h6 className="fw-bold text-dark">No category selected</h6>
                <p className="text-muted small mb-0">Choose a category to start building SKUs.</p>
            </div>
        );
    }

    if (attributes.length === 0) {
        return (
            <div className="text-center p-5 border border-warning rounded bg-warning bg-opacity-10">
                <i className="fas fa-exclamation-circle fa-2x text-warning mb-2"></i>
                <h6 className="fw-bold text-dark">No attributes available</h6>
                <p className="text-muted small mb-0"><strong>{selectedCategory?.title}</strong> does not have specific variant names.</p>
            </div>
        );
    }

    const addVariantRow = () => {
        const initialAttributes = {};
        attributes.forEach(attr => {
            const isColor = attr.name.toLowerCase() === 'color' || attr.name.toLowerCase() === 'colour';
            initialAttributes[attr._id] = { value: isColor ? "#000000" : "", price: "" };
        });

        setVariants(prev => [...prev, { sku: "", price: basePrice || "", stock: 0, image: "", attributes: initialAttributes }]);
    };

    const updateVariant = (index, field, val) => {
        setVariants(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: val };
            return updated;
        });
    };

    const updateAttributeField = (variantIndex, attrId, field, val) => {
        setVariants(prev => {
            const updated = [...prev];
            updated[variantIndex].attributes = {
                ...updated[variantIndex].attributes,
                [attrId]: { ...updated[variantIndex].attributes[attrId], [field]: val }
            };
            return updated;
        });
    };

    const removeVariant = (index) => {
        setVariants(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;
        setUploadingIndex(index);
        
        const formDataObj = new FormData();
        formDataObj.append("image", file);

        try {
            const response = await apiClient.post("/upload/productimage", formDataObj);
            
            const uploadedUrl = response?.data?.data?.data?.file || response?.data?.file || response?.data?.url;
            
            if (uploadedUrl) {
                updateVariant(index, "image", uploadedUrl);
                toast.success("Variant image uploaded!");
            } else {
                toast.error("Upload succeeded, but could not read URL from response.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to upload variant image.");
        } finally {
            setUploadingIndex(null);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded">
                <div>
                    <span className="fw-bold text-uppercase small text-muted">SKU Combinations</span>
                    <span className="badge bg-primary rounded-pill ms-2">{variants.length} items</span>
                </div>
                <button type="button" className="btn btn-sm btn-primary fw-bold" onClick={addVariantRow}>
                    <i className="fas fa-plus mr-1"></i> Add SKU Variant
                </button>
            </div>

            {variants.length === 0 ? (
                <p className="text-muted small font-italic text-center py-3">No combinations added. The base product details will be used.</p>
            ) : (
                variants.map((variant, index) => (
                    <div key={index} className="card border shadow-sm border-start border-4 border-primary">
                        <div className="card-body p-3 d-flex flex-column gap-3">

                            <div className="row g-2 align-items-end">
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted mb-1">SKU Code</label>
                                    <input type="text" className="form-control form-control-sm" placeholder="e.g. BAG-BLK-XL"
                                        value={variant.sku} onChange={(e) => updateVariant(index, "sku", e.target.value)} required />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted mb-1">Final Combo Price</label>
                                    <input type="number" className="form-control form-control-sm border-primary" min="0" step="0.01"
                                        value={variant.price} onChange={(e) => updateVariant(index, "price", e.target.value)} required />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted mb-1">Stock</label>
                                    <input type="number" className="form-control form-control-sm" min="0"
                                        value={variant.stock} onChange={(e) => updateVariant(index, "stock", e.target.value)} required />
                                </div>
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeVariant(index)}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <hr className="my-1 border-secondary-subtle" />

                            <div className="row g-2 align-items-center">
                                <div className="col-auto">
                                    <label className="position-relative border rounded bg-light d-flex align-items-center justify-content-center overflow-hidden"
                                        style={{ width: '50px', height: '50px', borderStyle: 'dashed', cursor: uploadingIndex === index ? 'wait' : 'pointer' }}>
                                        <input type="file" accept="image/*" hidden disabled={uploadingIndex === index} onChange={(e) => handleImageUpload(index, e.target.files[0])} />
                                        
                                        {uploadingIndex === index ? (
                                            <span className="spinner-border spinner-border-sm text-primary"></span>
                                        ) : variant.image ? (
                                            <img src={variant.image} alt="var" className="w-100 h-100 object-fit-cover" />
                                        ) : (
                                            <span className="small text-muted"><i className="fas fa-camera"></i></span>
                                        )}
                                    </label>
                                </div>

                                <div className="col d-flex flex-wrap gap-2">
                                    {attributes.map(attr => {
                                        const isColor = attr.name.toLowerCase() === 'color' || attr.name.toLowerCase() === 'colour';
                                        return (
                                            <div key={attr._id} className="bg-light border rounded p-2 d-flex flex-column gap-1">
                                                <span className="small fw-bold text-muted">{attr.name}</span>
                                                <div className="d-flex align-items-center gap-2">
                                                    {isColor ? (
                                                        <div className="d-flex align-items-center gap-1">
                                                            <input type="color" className="form-control form-control-color p-0 border-0" style={{ width: '24px', height: '24px' }}
                                                                value={variant.attributes[attr._id]?.value || "#000000"}
                                                                onChange={(e) => updateAttributeField(index, attr._id, "value", e.target.value)} />
                                                            <input type="text" className="form-control form-control-sm border-0 bg-transparent text-uppercase p-0" style={{ width: '65px' }}
                                                                placeholder="Hex" value={variant.attributes[attr._id]?.value || ""}
                                                                onChange={(e) => updateAttributeField(index, attr._id, "value", e.target.value)} />
                                                        </div>
                                                    ) : (
                                                        <input type="text" className="form-control form-control-sm border border-secondary-subtle p-1 px-2" style={{ width: '70px', fontWeight: 'bold' }}
                                                            placeholder="Value" value={variant.attributes[attr._id]?.value || ""}
                                                            onChange={(e) => updateAttributeField(index, attr._id, "value", e.target.value)} />
                                                    )}
                                                    <div className="input-group input-group-sm" style={{ width: '80px' }}>
                                                        <span className="input-group-text px-1 bg-white text-success fw-bold">+₹</span>
                                                        <input type="number" className="form-control px-1" placeholder="0" min="0" step="0.01"
                                                            value={variant.attributes[attr._id]?.price || ""}
                                                            onChange={(e) => updateAttributeField(index, attr._id, "price", e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

function ImageUploadZone({ images, onChange }) {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef();

    const handleUploadFiles = async (files) => {
        const fileArray = Array.from(files);
        if (!fileArray.length) return;

        const uploadLimit = 6 - images.length;
        const filesToUpload = fileArray.slice(0, uploadLimit);

        if (filesToUpload.length < fileArray.length) {
            toast.warning("Maximum limit is 6 images. Some files were skipped.");
        }

        if (filesToUpload.length === 0) return;

        setUploading(true);
        let newlyUploadedUrls = [];

        try {
            for (const file of filesToUpload) {
                const formDataObj = new FormData();
                formDataObj.append("image", file);

                const response = await apiClient.post("/upload/productimage", formDataObj);
                const uploadedUrl = response?.data?.data?.data?.file || response?.data?.file || response?.data?.url;
                
                if (uploadedUrl) {
                    newlyUploadedUrls.push(uploadedUrl);
                }
            }
            
            if (newlyUploadedUrls.length > 0) {
                onChange([...images, ...newlyUploadedUrls]);
                toast.success(`${newlyUploadedUrls.length} image(s) uploaded successfully!`);
            }
        } catch (error) {
            toast.error("Some images failed to upload.");
            console.error(error);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div
            className={`position-relative p-5 text-center rounded bg-light border border-2 ${dragging ? "border-primary bg-primary-subtle" : "border-secondary-subtle"}`}
            style={{ borderStyle: 'dashed', cursor: uploading ? 'wait' : 'pointer', minHeight: '160px' }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleUploadFiles(e.dataTransfer.files); }}
            onClick={() => !uploading && fileRef.current.click()}
        >
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleUploadFiles(e.target.files)} />

            {uploading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center rounded bg-white bg-opacity-75" style={{ zIndex: 10 }}>
                    <div className="spinner-border text-primary mb-2" role="status"></div>
                    <span className="fw-bold text-primary">Uploading Images...</span>
                </div>
            )}

            {images.length === 0 ? (
                <div>
                    <i className="fas fa-images fa-3x text-secondary mb-3"></i>
                    <h6 className="fw-bold text-dark">Drop images here or <span className="text-primary">browse</span></h6>
                    <p className="small text-muted mb-0">PNG, JPG up to 10MB · Max 6 images</p>
                </div>
            ) : (
                <div className="d-flex flex-wrap gap-2 justify-content-center" onClick={(e) => e.stopPropagation()}>
                    {images.map((src, i) => (
                        <div key={i} className="position-relative border rounded bg-white shadow-sm" style={{ width: '80px', height: '80px' }}>
                            <img src={src} alt="" className="w-100 h-100 object-fit-cover rounded" />
                            <button type="button" className="btn btn-dark btn-sm rounded-circle position-absolute top-0 start-100 translate-middle" style={{ width: '24px', height: '24px', padding: 0 }} onClick={() => onChange(images.filter((_, j) => j !== i))}>
                                &times;
                            </button>
                        </div>
                    ))}
                    {images.length < 6 && (
                        <div className="border rounded d-flex align-items-center justify-content-center text-secondary bg-white shadow-sm" style={{ width: '80px', height: '80px', borderStyle: 'dashed', cursor: 'pointer' }} onClick={() => fileRef.current.click()}>
                            <i className="fas fa-plus fa-lg"></i>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function AddProductPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const { categories, isLoading: categoriesLoading } = useCategories();
    const { data: initialData, isLoading: isFetchingProduct } = useProduct(id);
    const { createProduct, updateProduct, isCreating, isUpdating } = useProducts();

    // Changed country to initialize as an empty array
    const [formData, setFormData] = useState({
        title: "", description: "", price: "", comparePrice: "", category: "", country: [], images: [], status: "Active",
    });

    const [variants, setVariants] = useState([]);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                title: initialData.title || "", description: initialData.description || "", price: initialData.price || "",
                comparePrice: initialData.comparePrice || "", category: initialData.category?._id || initialData.category || "",
                // Support both arrays (new setup) or a string (legacy data) and convert to array
                country: Array.isArray(initialData.country) ? initialData.country : (initialData.country ? [initialData.country] : []), 
                images: initialData.images || [], status: initialData.status || "Active",
            });

            if (initialData.variants && initialData.variants.length > 0) {
                const parsedVariants = initialData.variants.map(v => {
                    const attrMap = {};
                    if (v.attributes) {
                        v.attributes.forEach(a => {
                            const attrId = a.name?._id || a.name;
                            attrMap[attrId] = { value: a.value, price: a.price || "" };
                        });
                    }
                    return {
                        sku: v.sku || "", price: v.price || "", stock: v.stock || 0, image: v.image || "", attributes: attrMap
                    };
                });
                setVariants(parsedVariants);
            }
        }
    }, [initialData, isEditing]);

    const set = (key, val) => setFormData((p) => ({ ...p, [key]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.category) {
            toast.error("Please select a category.");
            return;
        }

        const formattedVariants = variants.map(v => {
            const attributesArray = Object.keys(v.attributes).map(attrId => ({
                name: attrId, value: v.attributes[attrId].value, price: Number(v.attributes[attrId].price) || 0
            })).filter(attr => attr.value);

            return {
                sku: v.sku, price: Number(v.price) || Number(formData.price), stock: Number(v.stock) || 0,
                image: v.image || "", attributes: attributesArray
            };
        });

        const payload = {
            ...formData,
            price: Number(formData.price),
            comparePrice: formData.comparePrice ? Number(formData.comparePrice) : undefined,
            variants: formattedVariants
        };

        try {
            if (isEditing) await updateProduct({ id, data: payload });
            else await createProduct(payload);
            setSubmitted(true);
            toast.success("Product saved successfully!");
            setTimeout(() => navigate("/ecommerce/products"), 1000);
        } catch { 
            toast.error("Failed to save product.");
        }
    };

    const completeness = [formData.title, formData.price, formData.category, formData.images.length > 0, variants.length > 0].filter(Boolean).length;
    const progressPercent = (completeness / 5) * 100;
    const isSaving = isCreating || isUpdating;

    if (isFetchingProduct) return <div className="p-5 text-center mt-5"><div className="spinner-border text-primary"></div><p className="mt-2 text-muted">Loading product data...</p></div>;

    const categoryOptions = categories.map(c => ({ value: c._id, label: c.title }));
    const selectedCategoryOption = categoryOptions.find(opt => opt.value === formData.category) || null;
    
    // Filter to find all selected options matching the array of selected country values
    const selectedCountryOption = COUNTRY_OPTIONS.filter(opt => formData.country.includes(opt.value));

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided, backgroundColor: '#f8f9fa', borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none', padding: '2px', borderRadius: '0.375rem'
        }),
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="d-flex flex-wrap justify-content-between align-items-end mb-4">
                <div>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-1 small">
                            <li className="breadcrumb-item"><a href="#" className="text-decoration-none text-muted" onClick={(e) => { e.preventDefault(); navigate("/ecommerce/products"); }}>Products</a></li>
                            <li className="breadcrumb-item active text-dark fw-bold" aria-current="page">{isEditing ? "Edit Product" : "New Product"}</li>
                        </ol>
                    </nav>
                    <h2 className="mb-0 fw-bold">{isEditing ? "Edit Product" : "Add New Product"}</h2>
                </div>

                <div className="d-flex flex-column align-items-end" style={{ width: '200px' }}>
                    <small className="text-muted fw-bold mb-1">{completeness} of 5 fields complete</small>
                    <div className="progress w-100" style={{ height: '8px' }}>
                        <div className={`progress-bar ${progressPercent === 100 ? 'bg-success' : 'bg-primary'}`} role="progressbar" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row g-4">
                    <div className="col-lg-8 d-flex flex-column gap-4">

                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-3">
                                <div className="bg-primary-subtle text-primary rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="fas fa-box"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">Basic Information</h6>
                                    <p className="small text-muted mb-0">Product name, description & category</p>
                                </div>
                            </div>
                            <div className="card-body p-4 d-flex flex-column gap-3">
                                <div>
                                    <label className="form-label small fw-bold text-uppercase text-muted">Product Title <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control bg-light" placeholder="e.g. Classic Fit Cotton T-Shirt" value={formData.title} onChange={(e) => set("title", e.target.value)} required maxLength={100} />
                                </div>
                                <div>
                                    <label className="form-label small fw-bold text-uppercase text-muted">Description</label>
                                    <textarea className="form-control bg-light" rows="3" placeholder="Describe the product..." value={formData.description} onChange={(e) => set("description", e.target.value)} />
                                </div>
                                <div>
                                    <label className="form-label small fw-bold text-uppercase text-muted">Category <span className="text-danger">*</span></label>
                                    <Select
                                        options={categoryOptions} value={selectedCategoryOption}
                                        onChange={(selectedOption) => {
                                            set("category", selectedOption ? selectedOption.value : "");
                                            setVariants([]);
                                        }}
                                        isLoading={categoriesLoading} isClearable placeholder="— Select a category —" styles={customSelectStyles}
                                    />
                                </div>
                                
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label small fw-bold text-uppercase text-muted">Countries</label>
                                        <Select
                                            options={COUNTRY_OPTIONS} 
                                            value={selectedCountryOption}
                                            onChange={(selectedOptions) => set("country", selectedOptions ? selectedOptions.map(opt => opt.value) : [])}
                                            isMulti
                                            isClearable 
                                            placeholder="Select Countries" 
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-3">
                                <div className="bg-success-subtle text-success rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="fas fa-tag"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">Base Pricing</h6>
                                    <p className="small text-muted mb-0">Default pricing (can be overridden by SKU combinations)</p>
                                </div>
                            </div>
                            <div className="card-body p-4 row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-uppercase text-muted">Base Price <span className="text-danger">*</span></label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">₹</span>
                                        <input type="number" className="form-control bg-light" placeholder="0.00" min="0" step="0.01" value={formData.price} onChange={(e) => set("price", e.target.value)} required />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-uppercase text-muted">Compare-at Price</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">₹</span>
                                        <input type="number" className="form-control bg-light" placeholder="0.00" min="0" step="0.01" value={formData.comparePrice} onChange={(e) => set("comparePrice", e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-3">
                                <div className="bg-info-subtle text-info rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="fas fa-image"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">Product Images</h6>
                                    <p className="small text-muted mb-0">Up to 6 images · first image is the cover</p>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <ImageUploadZone images={formData.images} onChange={(imgs) => set("images", imgs)} />
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-3">
                                <div className="bg-warning-subtle text-warning rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="fas fa-sliders-h"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">SKU Combinations</h6>
                                    <p className="small text-muted mb-0">Create specific combinations to track price & stock</p>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <SkuCombinationBuilder
                                    categoryId={formData.category}
                                    categories={categories}
                                    variants={variants}
                                    setVariants={setVariants}
                                    basePrice={formData.price}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 d-flex flex-column gap-4">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-3">
                                <div className="bg-primary-subtle text-primary rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <i className="fas fa-paper-plane"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold">Publish</h6>
                                    <p className="small text-muted mb-0">Save & go live</p>
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-uppercase text-muted">Status</label>
                                    <div className="d-flex gap-2">
                                        <button type="button" className={`btn flex-grow-1 mr-2 fw-bold ${formData.status === "Active" ? "btn-success" : "btn-outline-secondary"}`} onClick={() => set("status", "Active")}>
                                            <i className="fas fa-check-circle mr-1"></i> Active
                                        </button>
                                        <button type="button" className={`btn flex-grow-1 fw-bold ${formData.status === "Inactive" ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => set("status", "Inactive")}>
                                            <i className="fas fa-times-circle mr-1"></i> Inactive
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" className={`btn w-100 py-2 fw-bold ${submitted ? "btn-success" : "btn-primary"}`} disabled={isSaving}>
                                    {isSaving ? (
                                        <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> {isEditing ? "Updating…" : "Publishing…"}</>
                                    ) : submitted ? (
                                        <><i className="fas fa-check mr-2"></i> {isEditing ? "Updated!" : "Published!"}</>
                                    ) : (
                                        <><i className="fas fa-save mr-2"></i> {isEditing ? "Update Product" : "Publish Product"}</>
                                    )}
                                </button>

                                <button type="button" className="btn btn-light border w-100 py-2 mt-2 fw-bold text-color-balck" onClick={() => navigate("/ecommerce/products")}>
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom py-3">
                                <h6 className="mb-0 fw-bold">Completion</h6>
                            </div>
                            <div className="card-body p-3">
                                <ul className="list-group list-group-flush small">
                                    {[
                                        ["Product title", !!formData.title],
                                        ["Base price set", !!formData.price],
                                        ["Category selected", !!formData.category],
                                        ["Images uploaded", formData.images.length > 0],
                                        ["Variants combinations added", variants.length > 0]
                                    ].map(([label, done]) => (
                                        <li key={label} className={`list-group-item bg-transparent border-0 px-0 py-1 d-flex align-items-center gap-2 ${done ? "text-success fw-bold" : "text-muted"}`}>
                                            <i className={`fas ${done ? 'fa-check-circle' : 'fa-circle text-light border rounded-circle'}`}></i> {label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}